// Store only a random request ID and a digest; never persist a PIN or payout data.
const pending = new Map<string, string>();
const inFlight = new Map<string, Promise<unknown>>();

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value)
    .filter(([, v]) => v !== undefined).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
    .map(([k, v]) => [k, canonical(v)]));
  return value;
}

export async function submitOnce<T>(
  scope: string,
  body: Record<string, unknown>,
  send: (requestId: string) => Promise<T>,
): Promise<T> {
  const { pin: _pin, ...order } = body;
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(canonical(order))));
  const hash = [...new Uint8Array(bytes)].map((v) => v.toString(16).padStart(2, '0')).join('');
  const storageKey = `jcqb:submission:${scope}:${hash}`;
  const active = inFlight.get(storageKey);
  if (active) return active as Promise<T>;
  let requestId = pending.get(storageKey);
  try { requestId ??= window.sessionStorage.getItem(storageKey) ?? undefined; } catch { /* In-memory fallback. */ }
  if (!requestId || !/^[A-Za-z0-9_-]{16,128}$/.test(requestId)) {
    requestId = [...crypto.getRandomValues(new Uint8Array(16))].map((v) => v.toString(16).padStart(2, '0')).join('');
  }
  pending.set(storageKey, requestId);
  try { window.sessionStorage.setItem(storageKey, requestId); } catch { /* Storage may be unavailable. */ }
  const clear = (): void => {
    pending.delete(storageKey);
    try { window.sessionStorage.removeItem(storageKey); } catch { /* In-memory state already cleared. */ }
  };
  const result = Promise.resolve().then(() => send(requestId)).then((value) => {
    clear();
    return value;
  }).catch((error: unknown) => {
    const status = (error as { status?: number } | null)?.status;
    // Timeouts and server/network errors can occur after the order committed.
    if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) clear();
    throw error;
  }).finally(() => { inFlight.delete(storageKey); });
  inFlight.set(storageKey, result);
  return result;
}
